import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { buildApiUrl } from '../utils/apiBase.js';
import { isDevHubContext } from '../utils/hubEnv.js';
import {
  createDefaultFlatNavVisibility,
  normalizeFlatNavVisibility,
} from '../config/hubNavItems.js';

const BETA_OVERRIDE_STORAGE_KEY = 'hub-beta-override';
const DEFAULT_TRIAL_PERIOD_DAYS = 14;
const DEFAULT_BETA_TRIAL_PERIOD_DAYS = 30;

const BetaContext = createContext(null);

/**
 * β版状態・ハブ設定の Provider
 * - Firestore config/hub-settings を Portal API 経由で取得
 * - 試用日数は管理画面のハブ設定で変更可能
 * - UserHub メニュー表示はサーバー DEPLOY_ENV 解決済みマップを利用
 * - 開発環境での β 一時切替は admin のみ（UI 表示・localStorage・API ヘッダー）
 */
export const BetaProvider = ({ children, user = null }) => {
  const canUseBetaOverride = isDevHubContext() && user?.role === 'admin';

  const [hubSettings, setHubSettings] = useState(null);
  const [betaDisabledByUser, setBetaDisabledByUserState] = useState(false);

  useEffect(() => {
    if (!canUseBetaOverride) {
      setBetaDisabledByUserState(false);
      return;
    }
    const stored = localStorage.getItem(BETA_OVERRIDE_STORAGE_KEY) === 'off';
    setBetaDisabledByUserState(stored);
  }, [canUseBetaOverride]);

  useEffect(() => {
    let cancelled = false;

    const fetchHubSettings = async () => {
      try {
        const res = await fetch(buildApiUrl('/api/config/hub-settings'), { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) {
          if (res.ok) {
            setHubSettings({
              isBetaPeriod: data.isBetaPeriod === true,
              trialPeriodDays: Number(data.trialPeriodDays) || DEFAULT_TRIAL_PERIOD_DAYS,
              betaTrialPeriodDays: Number(data.betaTrialPeriodDays) || DEFAULT_BETA_TRIAL_PERIOD_DAYS,
              userHubNavVisibility: normalizeFlatNavVisibility(data.userHubNavVisibility),
            });
          } else {
            setHubSettings({
              isBetaPeriod: false,
              trialPeriodDays: DEFAULT_TRIAL_PERIOD_DAYS,
              betaTrialPeriodDays: DEFAULT_BETA_TRIAL_PERIOD_DAYS,
              userHubNavVisibility: createDefaultFlatNavVisibility(),
            });
          }
        }
      } catch {
        if (!cancelled) {
          setHubSettings({
            isBetaPeriod: false,
            trialPeriodDays: DEFAULT_TRIAL_PERIOD_DAYS,
            betaTrialPeriodDays: DEFAULT_BETA_TRIAL_PERIOD_DAYS,
            userHubNavVisibility: createDefaultFlatNavVisibility(),
          });
        }
      }
    };

    fetchHubSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  const setBetaDisabledByUser = useCallback((value) => {
    if (!canUseBetaOverride) return;
    setBetaDisabledByUserState(value);
    if (typeof window !== 'undefined') {
      if (value) {
        localStorage.setItem(BETA_OVERRIDE_STORAGE_KEY, 'off');
      } else {
        localStorage.removeItem(BETA_OVERRIDE_STORAGE_KEY);
      }
    }
  }, [canUseBetaOverride]);

  const isBetaPeriod = hubSettings?.isBetaPeriod ?? null;
  const isHubSettingsReady = hubSettings !== null;
  const trialPeriodDays = hubSettings?.trialPeriodDays ?? DEFAULT_TRIAL_PERIOD_DAYS;
  const betaTrialPeriodDays = hubSettings?.betaTrialPeriodDays ?? DEFAULT_BETA_TRIAL_PERIOD_DAYS;
  const userHubNavVisibility = hubSettings?.userHubNavVisibility ?? createDefaultFlatNavVisibility();

  const effectiveIsBeta = useMemo(
    () => isBetaPeriod === true && !betaDisabledByUser,
    [isBetaPeriod, betaDisabledByUser]
  );

  const effectiveTrialPeriodDays = useMemo(
    () => (effectiveIsBeta ? betaTrialPeriodDays : trialPeriodDays),
    [effectiveIsBeta, betaTrialPeriodDays, trialPeriodDays]
  );

  /** 設定読込前は誤って本番 UI を出さないよう、β 制限と同様に扱う */
  const betaFeaturesRestricted = useMemo(
    () => !isHubSettingsReady || effectiveIsBeta,
    [isHubSettingsReady, effectiveIsBeta]
  );

  const showBetaToggle = canUseBetaOverride;

  const betaApiHeaders = useMemo(
    () => (betaDisabledByUser ? { 'X-Beta-Override': 'off' } : {}),
    [betaDisabledByUser]
  );

  const value = useMemo(
    () => ({
      isBetaPeriod,
      isHubSettingsReady,
      effectiveIsBeta,
      betaFeaturesRestricted,
      betaDisabledByUser,
      setBetaDisabledByUser,
      showBetaToggle,
      betaApiHeaders,
      trialPeriodDays,
      betaTrialPeriodDays,
      effectiveTrialPeriodDays,
      userHubNavVisibility,
    }),
    [
      isBetaPeriod,
      isHubSettingsReady,
      effectiveIsBeta,
      betaFeaturesRestricted,
      betaDisabledByUser,
      setBetaDisabledByUser,
      showBetaToggle,
      betaApiHeaders,
      trialPeriodDays,
      betaTrialPeriodDays,
      effectiveTrialPeriodDays,
      userHubNavVisibility,
    ]
  );

  return <BetaContext.Provider value={value}>{children}</BetaContext.Provider>;
};

export const useBeta = () => {
  const ctx = useContext(BetaContext);
  if (!ctx) {
    return {
      isBetaPeriod: null,
      isHubSettingsReady: false,
      effectiveIsBeta: false,
      betaFeaturesRestricted: true,
      betaDisabledByUser: false,
      setBetaDisabledByUser: () => {},
      showBetaToggle: false,
      betaApiHeaders: {},
      trialPeriodDays: DEFAULT_TRIAL_PERIOD_DAYS,
      betaTrialPeriodDays: DEFAULT_BETA_TRIAL_PERIOD_DAYS,
      effectiveTrialPeriodDays: DEFAULT_TRIAL_PERIOD_DAYS,
      userHubNavVisibility: createDefaultFlatNavVisibility(),
    };
  }
  return ctx;
};
